import { useState, useRef, useEffect } from "react";
import { Loader2, Check, Zap, Pencil, Trash2, Sparkles, SlidersHorizontal, Users, Hand, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { BrutalButton } from "@/components/Brutal";
import { PersonMultiSelect } from "@/components/Selects";
import { DUE_OPTIONS } from "@/lib/labels";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";

// Turn a Quick Tile template into a real task by only picking WHEN + WHO.
export default function QuickTileDialog({ tile, users, onClose, onDone, admin, onEdit }) {
  const tr = useT();
  const tileGroups = tile?.group_ids || [];
  const selectableUsers = (users || []).filter((u) => {
    if (u.role === "super_admin" || u.is_super_admin) return false;
    if (tileGroups.length === 0) return true;
    return (u.group_ids || []).some((g) => tileGroups.includes(g));
  });
  const [dueWhen, setDueWhen] = useState("vandaag");
  const [dueTime, setDueTime] = useState("");
  const [dueDate, setDueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [assignees, setAssignees] = useState([]);
  const [mode, setMode] = useState("default");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);
  useEffect(() => { setStep(0); }, [tile?.id]);
  const busyRef = useRef(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [imgUrl, setImgUrl] = useState(tile?.image_url || null);
  const [genBusy, setGenBusy] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const genIcon = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post(`/tasks/${tile.id}/generate-tile-image`);
      setImgUrl(data.url);
      toast.success(tr("Icoon gegenereerd! ✨"));
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || tr("Mislukt")); }
    finally { setGenBusy(false); }
  };

  const removeTile = async () => {
    setBusy(true);
    try {
      await api.delete(`/tasks/${tile.id}`);
      toast.success(tr("Quick Tile verwijderd"));
      onDone?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || tr("Mislukt")); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (busyRef.current) return;
    if (mode === "people" && assignees.length === 0) { toast.error(tr("Kies minstens één persoon")); return; }
    busyRef.current = true;
    setBusy(true);
    try {
      const payload = {
        due_when: dueWhen, due_time: dueWhen === "specifiek" ? dueTime : null,
        due_date: dueWhen !== "nu" ? dueDate : null,
      };
      if (mode === "people") {
        payload.assignee_ids = assignees;
        payload.assign_mode = assignees.length > 1 ? "per_person" : null;
      } else if (mode === "open") {
        payload.free_pickup = true;
      }
      await api.post(`/tasks/${tile.id}/instantiate`, payload);
      const todayISO = new Date().toISOString().slice(0, 10);
      const d1 = new Date(Date.now() + 864e5).toISOString().slice(0, 10);
      const d2 = new Date(Date.now() + 1728e5).toISOString().slice(0, 10);
      const dayLbl = dueDate === todayISO ? tr("vandaag") : dueDate === d1 ? tr("morgen") : dueDate === d2 ? tr("overmorgen") : dueDate;
      const timeLbl = dueWhen === "specifiek" && dueTime ? ` ${tr("om")} ${dueTime}` : "";
      const when = dueWhen === "nu" ? tr("nu") : `${dayLbl}${timeLbl}`;
      toast.success(`${tr("Ingepland voor")} ${when} 🎉`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || tr("Mislukt")); }
    finally { busyRef.current = false; setBusy(false); }
  };

  return (
    <Dialog open={!!tile} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="quicktile-dialog" style={{ backgroundColor: "#ffffff", opacity: 1 }} className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[90vh] overflow-auto backdrop-blur-none">
        <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2"><span className="rounded-lg p-1.5" style={{ backgroundColor: "#371F75" }}><Zap className="w-5 h-5 text-white" strokeWidth={2.5} /></span>{tile?.title}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Kies wanneer en voor wie deze Quick Tile ingepland wordt.</DialogDescription>
        {tile?.description && <p className="text-sm text-brand-ink/70">{tile.description}</p>}
        <p className="text-xs font-bold text-brand-ink/50">{tr("Stap")} {step + 1} {tr("van")} 2</p>
        {step === 0 && (<>
        {admin && (
          <div className="flex items-center gap-3 brutal-sm rounded-xl p-3 bg-brand-cream">
            {imgUrl
              ? <img src={imgUrl.startsWith("http") ? imgUrl : `${BACKEND}${imgUrl}`} alt="" className="w-14 h-14 rounded-lg object-cover bg-white shrink-0" />
              : <span className="w-14 h-14 rounded-lg bg-white flex items-center justify-center shrink-0"><Zap className="w-6 h-6 text-brand-ink/40" strokeWidth={2.5} /></span>}
            <div className="flex-1 min-w-0">
              <p className="font-head font-bold text-sm">{imgUrl ? tr("AI-icoon") : tr("Nog geen icoon")}</p>
              <p className="text-xs text-brand-ink/50">{tr("Geeft de tile meer herkenning")}</p>
            </div>
            <BrutalButton data-testid="qt-gen-icon" size="sm" variant="white" onClick={genIcon} disabled={genBusy}>
              {genBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={2.5} />}
              {imgUrl ? tr("Opnieuw") : tr("Genereer icoon")}
            </BrutalButton>
          </div>
        )}
        <div>
          <label className="font-head font-bold text-sm">{tr("Wanneer?")}</label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {DUE_OPTIONS.map((d) => (
              <button key={d.value} data-testid={`qt-due-${d.value}`} onClick={() => setDueWhen(d.value)}
                className={`brutal-sm brutal-press rounded-xl py-3 flex flex-col items-center gap-1 font-bold text-sm ${dueWhen === d.value ? d.color + " " + (d.text || "text-brand-ink") : "bg-white"}`}>
                <d.icon className="w-5 h-5" strokeWidth={2.5} />{tr(d.label)}
              </button>
            ))}
          </div>
          {dueWhen !== "nu" && (
            <div className={`mt-2 grid gap-2 ${dueWhen === "specifiek" ? "grid-cols-2" : "grid-cols-1"}`}>
              <div>
                <label className="text-xs font-bold text-brand-ink/60">{tr("Datum")}</label>
                <DatePicker testid="qt-due-date" value={dueDate} onChange={(v) => setDueDate(v)} className="mt-1" />
              </div>
              {dueWhen === "specifiek" && (
                <div>
                  <label className="text-xs font-bold text-brand-ink/60">{tr("Tijd")}</label>
                  <input data-testid="qt-due-time" type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)}
                    className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none mt-1" />
                </div>
              )}
            </div>
          )}
        </div>
        </>)}
        {step === 1 && (
        <div>
          <label className="font-head font-bold text-sm">{tr("Toewijzen?")}</label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {[
              { key: "default", label: tr("Standaard"), icon: SlidersHorizontal },
              { key: "people", label: tr("Aan personen"), icon: Users },
              { key: "open", label: tr("Vrij oppakken"), icon: Hand },
            ].map((m) => (
              <button key={m.key} data-testid={`qt-mode-${m.key}`} onClick={() => setMode(m.key)}
                className={`brutal-sm brutal-press rounded-xl py-3 px-1 flex flex-col items-center gap-1 font-bold text-xs ${mode === m.key ? "bg-brand-violet text-white" : "bg-white"}`}>
                <m.icon className="w-5 h-5" strokeWidth={2.5} />{m.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-brand-ink/60 mt-1.5 font-medium">
            {mode === "default" && tr("Zoals de tegel is ingesteld")}
            {mode === "people" && tr("1 persoon = 1 taak · meerdere personen = aparte taak per persoon")}
            {mode === "open" && tr("Eén taak zonder vaste persoon — iemand pakt 'm op met \"Ikke!\"")}
          </p>
          {mode === "people" && (
            <div className="mt-2">
              <PersonMultiSelect users={selectableUsers} value={assignees} onChange={setAssignees} testid="qt-assignee" placeholder={tr("Kies één of meer huisgenoten…")} />
            </div>
          )}
        </div>
        )}
        <div className="flex items-center justify-between gap-3 pt-1">
          <BrutalButton data-testid="qt-wiz-back" variant="white" disabled={step === 0} onClick={() => setStep(0)}><ChevronLeft className="w-4 h-4" strokeWidth={2.5} /> {tr("Vorige")}</BrutalButton>
          {step === 0 ? (
            <BrutalButton data-testid="qt-wiz-next" style={{ backgroundColor: "#371F75" }} className="text-white" onClick={() => setStep(1)}>{tr("Volgende")} <ChevronRight className="w-4 h-4" strokeWidth={2.5} /></BrutalButton>
          ) : (
            <BrutalButton data-testid="qt-create-btn" style={{ backgroundColor: "#371F75" }} className="text-white" disabled={busy} onClick={submit}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={3} />} {tr("Inplannen")}
            </BrutalButton>
          )}
        </div>

        {admin && (
          <div className="pt-3 mt-1 border-t-2 border-dashed border-brand-ink/10">
            {!confirmDel ? (
              <div className="flex gap-2">
                <BrutalButton data-testid="qt-edit-btn" variant="white" className="flex-1" onClick={() => { onEdit?.(tile); }}>
                  <Pencil className="w-4 h-4" strokeWidth={2.5} /> {tr("Bewerken")}
                </BrutalButton>
                <BrutalButton data-testid="qt-delete-btn" variant="pink" className="flex-1" onClick={() => setConfirmDel(true)}>
                  <Trash2 className="w-4 h-4" strokeWidth={2.5} /> {tr("Verwijderen")}
                </BrutalButton>
              </div>
            ) : (
              <div className="brutal-sm rounded-xl bg-brand-pink/10 p-3">
                <p className="text-sm font-bold mb-2">{tr("Deze Quick Tile definitief verwijderen?")}</p>
                <div className="flex gap-2">
                  <BrutalButton data-testid="qt-delete-cancel" variant="white" className="flex-1" onClick={() => setConfirmDel(false)}>{tr("Annuleren")}</BrutalButton>
                  <BrutalButton data-testid="qt-delete-confirm" variant="pink" className="flex-1" disabled={busy} onClick={removeTile}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" strokeWidth={2.5} />} {tr("Verwijderen")}
                  </BrutalButton>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
